import { useSelector } from '@xstate/react';
import React, { useContext, useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import {
  currentStepIndexSelector,
  firstAvailableStepSelector,
  IWizardContext,
  WizardContext,
} from '../contexts';
import ComparatorContext, {
  IComparatorContext,
  getNavigateBySlug,
  getNavigateByUrl,
  ComparatorStep,
} from '../contexts/comparator/comparator-context';
import { getIndexFromGoToEvent } from '../state-machines/wizard-machine';
import { ComparatorProps } from './Comparator';
import { WizardInterpreter } from './Wizard';
import { ComponentRenderer, Page, PageLayout } from './PageBuilder';

export interface RoutingProps {
  layout: ComparatorProps['layout'];
  steps: ComparatorProps['steps'];
}

// TODO: refactor that hook in order to make it react router v6 compliant
const useUrlRoutes = ({ steps, layout }: RoutingProps) => {
  return useMemo(() => {
    return steps.map((step) => {
      const pageIndex = layout.findIndex((l) => l.name === step.name);
      if (pageIndex < 0) return null;
      const page = layout[pageIndex];
      const children = page.components.map((c, i) => (
        <ComponentRenderer key={`${c.type}-${i}`} serializedComponent={c} />
      ));
      return (
        /* PageContext (localState of a page, typically where API calls happens or data shared by components are set) */
        <Route
          path={step.path}
          key={`route-${step.name}-${pageIndex}`}
          element={<Page {...page}>{children}</Page>}
        />
      );
    });
  }, [steps, layout]);
};

type RoutingOptions = RoutingProps & { comparator: IComparatorContext };

const SlugPage = ({
  comparator,
  path,
  ...layout
}: PageLayout & { path: string; comparator: IComparatorContext }) => {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('step') || comparator.defaultStep;
  const children = layout.components.map((c, i) => (
    <ComponentRenderer key={`${c.type}-${i}`} serializedComponent={c} />
  ));
  return <> {slug === path && <Page {...layout}>{children}</Page>}</>;
};

const SlugRouter = ({ steps, layout, comparator }: RoutingOptions) => {
  const pages = steps
    .map((step) => {
      const sliceStartIndex = step.path.indexOf('?');
      const path =
        sliceStartIndex > -1 ? step.path.slice(sliceStartIndex) : step.path;
      const pageLayout = layout.find((l) => l.name === step.name);
      if (!path || !pageLayout) return null;
      return (
        <SlugPage
          {...pageLayout}
          path={path}
          key={step.name}
          comparator={comparator}
        />
      );
    })
    .filter((p) => !!p);
  return <>{pages}</>;
};

const useSlugRoutes = (routingOptions: RoutingOptions) => {
  const { steps, layout, comparator } = routingOptions;
  return useMemo(() => {
    return (
      <Route
        path={comparator.rootUrl}
        element={<SlugRouter {...routingOptions} />}
      />
    );
  }, [steps, layout, comparator.rootUrl]);
};

const useComparatorRoutes = (routingOptions: RoutingOptions) => {
  if (routingOptions.comparator.navigationMode === 'url') {
    return useUrlRoutes(routingOptions);
  } else {
    return useSlugRoutes(routingOptions);
  }
};

const useRedirection = (
  wizardService: WizardInterpreter,
  navigate: IComparatorContext['navigate'],
  steps: ComparatorStep[],
  comparator: IComparatorContext
) => {
  const currentIndex = useSelector(wizardService, currentStepIndexSelector);
  const firstAvailableIndex = useSelector(
    wizardService,
    firstAvailableStepSelector
  );
  useEffect(() => {
    const shouldRedirect =
      !steps[currentIndex].skipPreviousFieldErrors &&
      currentIndex > firstAvailableIndex;

    if (shouldRedirect && navigate && comparator.allModulesLoaded)
      navigate({ path: steps[firstAvailableIndex].path });
  }, [comparator.allModulesLoaded, navigate]);
};

export const Routing: React.FC<RoutingProps> = ({ layout, steps }) => {
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  const comparator = useContext<IComparatorContext>(ComparatorContext);
  if (!wizardService) return null;
  const reactRouterNavigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const navigate = useMemo(() => {
    return comparator.navigationMode === 'url'
      ? getNavigateByUrl(reactRouterNavigate)
      : getNavigateBySlug(
          reactRouterNavigate,
          setSearchParams,
          comparator.rootUrl
        );
  }, [comparator.rootUrl]);
  useRedirection(wizardService, navigate, steps, comparator);
  useEffect(() => {
    if (comparator.setComparatorContext && navigate !== comparator.navigate) {
      comparator.setComparatorContext((ctx) => ({
        ...ctx,
        navigate,
      }));
    }
  }, []);

  useEffect(() => {
    const navigationListener = (s, e) => {
      if (e.type === 'GOTO' && comparator.navigate) {
        const index = getIndexFromGoToEvent(s.context.initialSteps, e);
        if (index > -1 && e.meta?.navigate !== false) {
          comparator.navigate(
            { path: steps[index].path },
            { reload: comparator.name === 'car' ? true : !!e.meta?.reload }
          );
        }
      }
    };
    wizardService?.onTransition(navigationListener);
    return () => {
      wizardService?.off(navigationListener);
    };
  }, [comparator.navigate]);

  const ComparatorRoutes = useComparatorRoutes({
    layout,
    steps,
    comparator,
  });

  if (navigate !== comparator.navigate) return null;
  return <Routes>{ComparatorRoutes}</Routes>;
};
